// frontend/src/utils/authTestUtils.js

/**
 * Utilities for testing and monitoring authentication behavior
 */

class AuthMonitor {
  constructor() {
    this.requestLog = [];
    this.startTime = Date.now();
    this.originalFetch = window.fetch;
    this.isMonitoring = false;
  }

  startMonitoring() {
    if (this.isMonitoring) return;
    
    this.isMonitoring = true;
    this.requestLog = [];
    this.startTime = Date.now();
    
    console.log('[AuthMonitor] Starting authentication monitoring...');
    
    window.fetch = (...args) => {
      const url = args[0];
      const options = args[1] || {};
      
      if (typeof url === 'string' && url.includes('/auth/')) {
        const logEntry = {
          timestamp: Date.now() - this.startTime,
          url: url.replace(window.location.origin, ''),
          method: options.method || 'GET',
          hasAuth: !!options.headers?.Authorization,
          stack: new Error().stack
        };
        
        this.requestLog.push(logEntry);
        console.log(`[AuthMonitor] ${logEntry.method} ${logEntry.url} (${logEntry.timestamp}ms)`);
      }
      
      return this.originalFetch.apply(window, args);
    };
  }

  stopMonitoring() {
    if (!this.isMonitoring) return;
    
    this.isMonitoring = false;
    window.fetch = this.originalFetch;
    
    console.log('[AuthMonitor] Stopped monitoring. Summary:');
    this.printSummary();
  }

  printSummary() {
    const totalRequests = this.requestLog.length;
    const uniqueEndpoints = [...new Set(this.requestLog.map(r => r.url))];
    const duplicateRequests = totalRequests - uniqueEndpoints.length;
    
    console.group('[AuthMonitor] Summary');
    console.log(`Total auth requests: ${totalRequests}`);
    console.log(`Unique endpoints: ${uniqueEndpoints.length}`);
    console.log(`Duplicate requests: ${duplicateRequests}`);
    console.log(`Time span: ${Date.now() - this.startTime}ms`);
    
    if (this.requestLog.length > 0) {
      console.log('\nRequest timeline:');
      this.requestLog.forEach((req, index) => {
        console.log(`${index + 1}. ${req.timestamp}ms: ${req.method} ${req.url}`);
      });
    }
    
    if (duplicateRequests > 0) {
      console.warn(`⚠️ Found ${duplicateRequests} duplicate requests - optimization needed!`);
    } else {
      console.log('✅ No duplicate requests detected');
    }
    
    console.groupEnd();
  }

  getReport() {
    return {
      totalRequests: this.requestLog.length,
      uniqueEndpoints: [...new Set(this.requestLog.map(r => r.url))].length,
      duplicateRequests: this.requestLog.length - [...new Set(this.requestLog.map(r => r.url))].length,
      timeSpan: Date.now() - this.startTime,
      requests: [...this.requestLog]
    };
  }
}

/**
 * Test page load authentication behavior
 */
export const testPageLoadAuth = () => {
  const monitor = new AuthMonitor();
  
  console.log('🧪 Testing page load authentication behavior...');
  monitor.startMonitoring();
  
  // Stop monitoring after 5 seconds
  setTimeout(() => {
    monitor.stopMonitoring();
    const report = monitor.getReport();
    
    // Evaluate results
    if (report.totalRequests <= 2) {
      console.log('✅ PASS: Minimal auth requests on page load');
    } else if (report.totalRequests <= 5) {
      console.log('⚠️ WARN: Moderate auth requests - could be optimized');
    } else {
      console.log('❌ FAIL: Too many auth requests - optimization needed');
    }
    
    return report;
  }, 5000);
  
  return monitor;
};

/**
 * Test page visibility change behavior
 */
export const testPageVisibilityAuth = () => {
  const monitor = new AuthMonitor();
  
  console.log('🧪 Testing page visibility authentication behavior...');
  console.log('👁️ Hide and show the page tab to test...');
  
  monitor.startMonitoring();
  
  let visibilityChanges = 0;
  const originalHandler = document.onvisibilitychange;
  
  document.onvisibilitychange = () => {
    visibilityChanges++;
    console.log(`[AuthMonitor] Visibility change #${visibilityChanges}: ${document.visibilityState}`);
    
    if (originalHandler) {
      originalHandler();
    }
  };
  
  // Stop monitoring after 30 seconds
  setTimeout(() => {
    monitor.stopMonitoring();
    document.onvisibilitychange = originalHandler;
    
    const report = monitor.getReport();
    console.log(`👁️ Detected ${visibilityChanges} visibility changes`);
    
    if (report.totalRequests <= visibilityChanges * 2) {
      console.log('✅ PASS: Reasonable auth requests for visibility changes');
    } else {
      console.log('❌ FAIL: Too many auth requests for visibility changes');
    }
    
    return report;
  }, 30000);
  
  return monitor;
};

/**
 * Simulate rapid auth requests to test throttling
 */
export const testAuthThrottling = async () => {
  console.log('🧪 Testing authentication throttling...');
  
  const monitor = new AuthMonitor();
  monitor.startMonitoring();
  
  // Make 5 rapid auth requests
  const promises = [];
  for (let i = 0; i < 5; i++) {
    promises.push(
      fetch('/api/v1/auth/me', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      }).catch(() => {}) // Ignore errors for this test
    );
  }
  
  await Promise.all(promises);
  
  setTimeout(() => {
    monitor.stopMonitoring();
    const report = monitor.getReport();
    
    if (report.totalRequests < 5) {
      console.log('✅ PASS: Throttling is working - some requests were prevented');
    } else {
      console.log('❌ FAIL: No throttling detected - all requests went through');
    }
    
    return report;
  }, 1000);
};

/**
 * Global auth monitor instance
 */
export const authMonitor = new AuthMonitor();

/**
 * Add to window for easy access in dev tools
 */
if (process.env.NODE_ENV === 'development') {
  window.authTestUtils = {
    testPageLoadAuth,
    testPageVisibilityAuth,
    testAuthThrottling,
    authMonitor
  };
  
  console.log('🔧 Auth test utilities available at window.authTestUtils');
  console.log('📖 Usage:');
  console.log('  window.authTestUtils.testPageLoadAuth()');
  console.log('  window.authTestUtils.testPageVisibilityAuth()');
  console.log('  window.authTestUtils.testAuthThrottling()');
}
